import { useMemo, useState } from 'react';

import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

import MainCard from 'components/MainCard';
import apiClient from 'utils/apiClient';
import useGet from 'hooks/useGet';
import { useAuth } from 'pages/AuthProvider';
import StaffUserDialog from './roles/StaffUserDialog';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

const statusColorMap = {
  created: 'default',
  pending: 'warning',
  authorized: 'info',
  captured: 'success',
  failed: 'error',
  cancelled: 'default',
  refunded: 'secondary'
};

const paymentStatuses = ['created', 'pending', 'authorized', 'captured', 'failed', 'cancelled', 'refunded'];

const collectionModeOptions = [
  { label: 'All Lists', value: 'all' },
  { label: 'Cart Only', value: 'cart' },
  { label: 'Wishlist Only', value: 'wishlist' }
];

const userStatusOptions = [
  { label: 'All statuses', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' }
];

const userRoleOptions = [
  { label: 'All roles', value: 'all' },
  { label: 'Admin', value: 'admin' },
  { label: 'Customer', value: 'customer' },
  { label: 'Subscriber', value: 'subscriber' },
  { label: 'User', value: 'user' }
];

const tabLabels = {
  orders: 'Orders',
  collections: 'Cart & Wishlist'
};

const formatAmount = (currency, value) => {
  const amount = Number(value || 0);
  const normalizedCurrency = String(currency || 'INR').toUpperCase();
  const locale = normalizedCurrency === 'USD' ? 'en-US' : 'en-IN';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: normalizedCurrency,
      maximumFractionDigits: 2
    }).format(amount);
  } catch {
    return `${normalizedCurrency} ${amount.toFixed(2)}`;
  }
};

const formatDateTime = (value) => {
  if (!value) return '-';

  try {
    return new Date(value).toLocaleString();
  } catch {
    return '-';
  }
};

const getDisplayName = (user) => user?.name || user?.email || 'Selected User';

const getUserStatusChip = (user) => (
  <Chip size="small" label={user?.isActive ? 'Active' : 'Inactive'} color={user?.isActive ? 'success' : 'default'} />
);

const objectEntries = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  return Object.entries(value).filter(([, entryValue]) => entryValue !== undefined && entryValue !== null && entryValue !== '');
};

function AttributeChips({ value, emptyLabel = 'No attributes' }) {
  const entries = objectEntries(value);

  if (!entries.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
      {entries.map(([key, entryValue]) => (
        <Chip key={`${key}-${String(entryValue)}`} size="small" variant="outlined" label={`${key}: ${entryValue}`} />
      ))}
    </Stack>
  );
}

function DetailMetric({ label, value, tone = 'default' }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        minWidth: 150,
        flex: '1 1 160px',
        borderColor: tone === 'highlight' ? 'primary.light' : 'divider'
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="subtitle2">{value}</Typography>
    </Paper>
  );
}

function ProductIdentity({ item }) {
  const title = item?.title || item?.product?.title || 'Untitled Product';
  const sku = item?.sku || item?.product?.SKU || 'No SKU';
  const image = item?.heroImage || item?.product?.heroImage || null;

  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 220 }}>
      <Avatar
        variant="rounded"
        src={image || undefined}
        alt={title}
        sx={{ width: 48, height: 48, bgcolor: 'grey.100', color: 'text.secondary' }}
      >
        {title.charAt(0).toUpperCase()}
      </Avatar>
      <Box>
        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {sku}
        </Typography>
        {item?.product?.isPublished === false && <Chip size="small" color="warning" label="Unpublished" sx={{ mt: 0.75 }} />}
      </Box>
    </Stack>
  );
}

function CartPricing({ item, currency }) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">Unit: {formatAmount(currency || 'INR', item?.unitPrice)}</Typography>
      <Typography variant="body2">Line: {formatAmount(currency || 'INR', item?.lineTotal)}</Typography>
      {Number(item?.usUnitPrice || 0) > 0 && (
        <Typography variant="body2" color="text.secondary">
          USD Unit: {formatAmount('USD', item?.usUnitPrice)}
        </Typography>
      )}
      {Number(item?.usLineTotal || 0) > 0 && (
        <Typography variant="body2" color="text.secondary">
          USD Line: {formatAmount('USD', item?.usLineTotal)}
        </Typography>
      )}
    </Stack>
  );
}

function WishlistPricing({ item }) {
  const fallbackBasePrice = item?.product?.price ?? 0;
  const fallbackUsdPrice = item?.product?.dollarPrice ?? 0;
  const primaryPrice = Number(item?.unitPrice ?? fallbackBasePrice ?? 0);
  const secondaryPrice = Number(fallbackUsdPrice || 0);

  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">{formatAmount('INR', primaryPrice)}</Typography>
      {secondaryPrice > 0 && (
        <Typography variant="body2" color="text.secondary">
          {formatAmount('USD', secondaryPrice)}
        </Typography>
      )}
    </Stack>
  );
}

function CartSection({ cart, isSummaryOnly }) {
  if (isSummaryOnly) {
    return (
      <Alert severity="info" variant="outlined">
        Cart details are summarized in this filter. Switch to &quot;All Lists&quot; to inspect item-level cart data.
      </Alert>
    );
  }

  if (!cart) {
    return (
      <Alert severity="info" variant="outlined">
        No cart found for this user.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
        <DetailMetric label="Currency" value={cart.currency || 'INR'} />
        <DetailMetric label="Items" value={cart.summary?.itemCount || cart.items?.length || 0} />
        <DetailMetric label="Subtotal" value={formatAmount(cart.currency || 'INR', cart.summary?.subtotal)} />
        <DetailMetric label="Shipping" value={formatAmount(cart.currency || 'INR', cart.summary?.shipping)} />
        <DetailMetric label="Total" value={formatAmount(cart.currency || 'INR', cart.summary?.total)} tone="highlight" />
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Configuration</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Pricing</TableCell>
              <TableCell>Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(cart.items || []).map((item) => (
              <TableRow key={item._id}>
                <TableCell>
                  <ProductIdentity item={item} />
                </TableCell>
                <TableCell sx={{ minWidth: 240 }}>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Selected
                      </Typography>
                      <AttributeChips value={item.selectedAttributes} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Variant
                      </Typography>
                      <AttributeChips value={item.variant?.attributes} emptyLabel="No variant attributes" />
                    </Box>
                    {objectEntries(item.variant?.otherAttributes).length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Other
                        </Typography>
                        <AttributeChips value={item.variant?.otherAttributes} emptyLabel="No extra attributes" />
                      </Box>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{Number(item.quantity || 0)}</TableCell>
                <TableCell>
                  <CartPricing item={item} currency={cart.currency} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{formatDateTime(item.updatedAt)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Added {formatDateTime(item.createdAt)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {!cart.items?.length && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No cart items found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function WishlistSection({ wishlist, isSummaryOnly }) {
  if (isSummaryOnly) {
    return (
      <Alert severity="info" variant="outlined">
        Wishlist details are summarized in this filter. Switch to &quot;All Lists&quot; to inspect item-level wishlist data.
      </Alert>
    );
  }

  if (!wishlist) {
    return (
      <Alert severity="info" variant="outlined">
        No wishlist found for this user.
      </Alert>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Product</TableCell>
            <TableCell>Configuration</TableCell>
            <TableCell>Price</TableCell>
            <TableCell>Updated</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(wishlist.items || []).map((item) => (
            <TableRow key={item._id}>
              <TableCell>
                <ProductIdentity item={item} />
              </TableCell>
              <TableCell sx={{ minWidth: 240 }}>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Selected
                    </Typography>
                    <AttributeChips value={item.selectedAttributes} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Variant
                    </Typography>
                    <AttributeChips value={item.variant?.attributes} emptyLabel="No variant attributes" />
                  </Box>
                  {objectEntries(item.variant?.otherAttributes).length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Other
                      </Typography>
                      <AttributeChips value={item.variant?.otherAttributes} emptyLabel="No extra attributes" />
                    </Box>
                  )}
                </Stack>
              </TableCell>
              <TableCell>
                <WishlistPricing item={item} />
              </TableCell>
              <TableCell>
                <Typography variant="body2">{formatDateTime(item.updatedAt)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Added {formatDateTime(item.createdAt)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
          {!wishlist.items?.length && (
            <TableRow>
              <TableCell colSpan={4} align="center">
                No wishlist items found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function Users() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [statusDrafts, setStatusDrafts] = useState({});
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [ordersPage, setOrdersPage] = useState(0);
  const [ordersRowsPerPage, setOrdersRowsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState('orders');
  const [listType, setListType] = useState('all');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const { roles, canAccess } = useAuth();
  const [roleAnchorEl, setRoleAnchorEl] = useState(null);
  const [roleChangeUserId, setRoleChangeUserId] = useState(null);
  const [updatingRole, setUpdatingRole] = useState(false);
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);

  const handleOpenRoleMenu = (event, user) => {
    event.stopPropagation();
    setRoleAnchorEl(event.currentTarget);
    setRoleChangeUserId(user._id);
  };

  const handleRoleSelect = async (newRoleKey) => {
    if (!roleChangeUserId || !newRoleKey) return;
    setUpdatingRole(true);
    try {
      await apiClient.patch(`/api/admin/users/${roleChangeUserId}/role`, { role: newRoleKey });
      await mutate();
    } catch (err) {
      console.error('Failed to update role', err);
    } finally {
      setUpdatingRole(false);
      setRoleAnchorEl(null);
      setRoleChangeUserId(null);
    }
  };

  const hasUserFilters =
    userStatusFilter !== 'all' || userRoleFilter !== 'all' || (viewMode === 'collections' && listType !== 'all');
  const activeUserFilterCount =
    Number(userStatusFilter !== 'all') + Number(userRoleFilter !== 'all') + Number(viewMode === 'collections' && listType !== 'all');
  const isFilterPopoverOpen = Boolean(filterAnchorEl);

  const endpoint = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (search.trim()) {
      params.set('search', search.trim());
    }
    if (userStatusFilter !== 'all') {
      params.set('isActive', String(userStatusFilter === 'active'));
    }
    if (userRoleFilter !== 'all') {
      params.set('role', userRoleFilter);
    }
    if (viewMode === 'collections') {
      params.set('listType', listType);
      return `/api/admin/users/cart-wishlist?${params.toString()}`;
    }
    return `/api/admin/users?${params.toString()}`;
  }, [page, limit, listType, search, userRoleFilter, userStatusFilter, viewMode]);

  const { data, loading, error, mutate } = useGet(endpoint);

  const users = data?.users || [];
  const displayedUsers = useMemo(
    () =>
      users.filter((user) => {
        if (userStatusFilter !== 'all' && Boolean(user?.isActive) !== (userStatusFilter === 'active')) return false;
        if (userRoleFilter !== 'all' && String(user?.role || 'user').toLowerCase() !== userRoleFilter) return false;
        return true;
      }),
    [userRoleFilter, userStatusFilter, users]
  );
  const pagination = data?.pagination || {};
  const selectedUser = displayedUsers.find((user) => user._id === selectedUserId) || null;
  const selectedOrders = useMemo(() => selectedUser?.orders || [], [selectedUser]);

  const selectedOrdersPaginated = useMemo(() => {
    const start = ordersPage * ordersRowsPerPage;
    return selectedOrders.slice(start, start + ordersRowsPerPage);
  }, [ordersPage, ordersRowsPerPage, selectedOrders]);

  const pageTitle = viewMode === 'orders' ? 'Users With Orders' : 'Users With Cart & Wishlist';
  const totalLabel = hasUserFilters ? displayedUsers.length : pagination.totalUsers || users.length || 0;

  const resetSelection = () => {
    setSelectedUserId(null);
    setOrdersPage(0);
  };

  const onSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
    setStatusDrafts({});
    resetSelection();
  };

  const onModeChange = (_event, nextMode) => {
    if (!nextMode || nextMode === viewMode) return;
    setViewMode(nextMode);
    setPage(1);
    setStatusDrafts({});
    resetSelection();
  };

  const onCollectionFilterChange = (event) => {
    setListType(event.target.value);
    setPage(1);
    resetSelection();
  };

  const onUserStatusFilterChange = (event) => {
    setUserStatusFilter(event.target.value);
    setPage(1);
    resetSelection();
  };

  const onUserRoleFilterChange = (event) => {
    setUserRoleFilter(event.target.value);
    setPage(1);
    resetSelection();
  };

  const clearUserFilters = () => {
    setUserStatusFilter('all');
    setUserRoleFilter('all');
    setListType('all');
    setPage(1);
    resetSelection();
  };

  const getDraftStatus = (order) => statusDrafts[order._id] || String(order?.payment?.status || '').toLowerCase() || 'created';

  const onUpdatePaymentStatus = async (order) => {
    const nextStatus = getDraftStatus(order);
    if (!order?._id || !nextStatus) return;

    setUpdatingOrderId(order._id);
    try {
      await apiClient.patch(`/api/payments/admin/orders/${order._id}/payment-status`, {
        paymentStatus: nextStatus
      });
      await mutate();
    } catch {
      // handled through existing fetch refresh; keeping UI stable here
    } finally {
      setUpdatingOrderId(null);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2}>
        <Typography variant="h4">Users Explorer</Typography>
        {canAccess('users', 'create') && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<PersonAddIcon />}
            onClick={() => setStaffDialogOpen(true)}
          >
            Generate Staff User
          </Button>
        )}
      </Stack>

      <MainCard>
        <Stack spacing={2}>
          <Tabs
            value={viewMode}
            onChange={onModeChange}
            variant="fullWidth"
            sx={{
              minHeight: 44,
              '& .MuiTab-root': {
                minHeight: 44,
                textTransform: 'none',
                fontWeight: 600
              }
            }}
          >
            <Tab value="orders" label={tabLabels.orders} />
            <Tab value="collections" label={tabLabels.collections} />
          </Tabs>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField fullWidth size="small" label="Search by name, email or phone" value={search} onChange={onSearchChange} />
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Filter users">
                <IconButton
                  color={hasUserFilters ? 'primary' : 'default'}
                  aria-label="Filter users"
                  aria-haspopup="true"
                  aria-expanded={isFilterPopoverOpen ? 'true' : undefined}
                  onClick={(event) => setFilterAnchorEl(event.currentTarget)}
                  sx={{ border: '1px solid', borderColor: hasUserFilters ? 'primary.main' : 'divider' }}
                >
                  <Badge color="primary" badgeContent={activeUserFilterCount} invisible={!hasUserFilters}>
                    <FilterListIcon fontSize="small" />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Chip
                size="small"
                label={hasUserFilters ? `${activeUserFilterCount} filter${activeUserFilterCount > 1 ? 's' : ''}` : 'Add filter'}
                color={hasUserFilters ? 'primary' : 'default'}
                variant="outlined"
              />
            </Stack>
            <Popover
              open={isFilterPopoverOpen}
              anchorEl={filterAnchorEl}
              onClose={() => setFilterAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { mt: 1, p: 2, width: { xs: 300, sm: 360 }, maxWidth: 'calc(100vw - 32px)' } } }}
            >
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Add Filter
                </Typography>
                <FormControl size="small" fullWidth>
                  <InputLabel id="users-status-filter-label">Status</InputLabel>
                  <Select
                    labelId="users-status-filter-label"
                    label="Status"
                    value={userStatusFilter}
                    onChange={onUserStatusFilterChange}
                  >
                    {userStatusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel id="users-role-filter-label">Role</InputLabel>
                  <Select
                    labelId="users-role-filter-label"
                    label="Role"
                    value={userRoleFilter}
                    onChange={onUserRoleFilterChange}
                  >
                    {userRoleOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {viewMode === 'collections' && (
                  <FormControl size="small" fullWidth>
                    <InputLabel id="users-list-filter-label">List Filter</InputLabel>
                    <Select
                      labelId="users-list-filter-label"
                      label="List Filter"
                      value={listType}
                      onChange={onCollectionFilterChange}
                    >
                      {collectionModeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Button size="small" color="inherit" onClick={clearUserFilters} disabled={!hasUserFilters}>
                    Clear filters
                  </Button>
                  <Button size="small" variant="contained" onClick={() => setFilterAnchorEl(null)}>
                    Apply
                  </Button>
                </Stack>
              </Stack>
            </Popover>
            {hasUserFilters && (
              <Button size="small" color="inherit" onClick={clearUserFilters} sx={{ minWidth: 110 }}>
                Clear
              </Button>
            )}
          </Stack>
        </Stack>
      </MainCard>

      {loading && <Typography>Loading users...</Typography>}
      {error && <Alert severity="error">Failed to load users data.</Alert>}

      {!loading && !error && (
        <MainCard
          title={`${pageTitle} (${totalLabel})`}
          secondary={
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip size="small" label={`Page ${pagination.page || 1} / ${pagination.totalPages || 1}`} color="default" />
              {viewMode === 'collections' && (
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={collectionModeOptions.find((option) => option.value === listType)?.label || 'All Lists'}
                />
              )}
            </Stack>
          }
        >
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  {viewMode === 'orders' ? (
                    <>
                      <TableCell>Total Orders</TableCell>
                      <TableCell>Captured</TableCell>
                      <TableCell>Total Spent</TableCell>
                      <TableCell align="right">Orders</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>Cart</TableCell>
                      <TableCell>Wishlist</TableCell>
                      <TableCell>Last Activity</TableCell>
                      <TableCell align="right">Lists</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedUsers.map((user) => {
                  const isSelected = selectedUserId === user._id;

                  if (viewMode === 'orders') {
                    const summary = user.orderSummary || {};

                    return (
                      <TableRow hover key={user._id}>
                        <TableCell>{user.name || '-'}</TableCell>
                        <TableCell>{user.email || '-'}</TableCell>
                        <TableCell>{user.phone || '-'}</TableCell>
                        <TableCell>
                          <Tooltip title={canAccess('users', 'edit') ? 'Click to change role' : 'User role'}>
                            <Chip
                              size="small"
                              label={user.role || 'user'}
                              color={user.role === 'admin' || user.role === 'superadmin' ? 'primary' : 'default'}
                              onClick={canAccess('users', 'edit') ? (e) => handleOpenRoleMenu(e, user) : undefined}
                              sx={{ cursor: canAccess('users', 'edit') ? 'pointer' : 'default' }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>{getUserStatusChip(user)}</TableCell>
                        <TableCell>{summary.totalOrders || 0}</TableCell>
                        <TableCell>{summary.capturedOrders || 0}</TableCell>
                        <TableCell>{formatAmount('INR', summary.totalSpent || 0)}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant={isSelected ? 'contained' : 'outlined'}
                            onClick={() => {
                              setSelectedUserId(user._id);
                              setOrdersPage(0);
                            }}
                          >
                            View Orders
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  const cartSummary = user.cartSummary || {};
                  const wishlistSummary = user.wishlistSummary || {};

                  return (
                    <TableRow hover key={user._id}>
                      <TableCell>{user.name || '-'}</TableCell>
                      <TableCell>{user.email || '-'}</TableCell>
                      <TableCell>{user.phone || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={canAccess('users', 'edit') ? 'Click to change role' : 'User role'}>
                          <Chip
                            size="small"
                            label={user.role || 'user'}
                            color={user.role === 'admin' || user.role === 'superadmin' ? 'primary' : 'default'}
                            onClick={canAccess('users', 'edit') ? (e) => handleOpenRoleMenu(e, user) : undefined}
                            sx={{ cursor: canAccess('users', 'edit') ? 'pointer' : 'default' }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>{getUserStatusChip(user)}</TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">{cartSummary.itemCount || 0} items</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {cartSummary.hasCart
                              ? formatAmount(user.cartSummary?.currency || 'INR', cartSummary.total || 0)
                              : 'No active cart'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{wishlistSummary.itemCount || 0} items</Typography>
                      </TableCell>
                      <TableCell>{formatDateTime(user.lastCollectionActivity)}</TableCell>
                      <TableCell align="right">
                        <Button size="small" variant={isSelected ? 'contained' : 'outlined'} onClick={() => setSelectedUserId(user._id)}>
                          View Lists
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!displayedUsers.length && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No users found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={Number(hasUserFilters ? displayedUsers.length : pagination.totalUsers || 0)}
            page={Math.max(0, Number((pagination.page || page) - 1))}
            onPageChange={(_event, nextPage) => {
              setPage(nextPage + 1);
              resetSelection();
            }}
            rowsPerPage={limit}
            onRowsPerPageChange={(event) => {
              setLimit(Number(event.target.value));
              setPage(1);
              resetSelection();
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </MainCard>
      )}

      {!loading && !error && selectedUser && viewMode === 'orders' && (
        <MainCard title={`Orders for ${getDisplayName(selectedUser)}`}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Update Payment Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedOrdersPaginated.map((order) => (
                  <TableRow key={order._id}>
                    <TableCell>{order._id}</TableCell>
                    <TableCell>{order.payment?.provider || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={order.payment?.status || 'unknown'}
                        color={statusColorMap[order.payment?.status] || 'default'}
                      />
                    </TableCell>
                    <TableCell>{formatAmount(order.currency, order.amount?.total)}</TableCell>
                    <TableCell>{formatDateTime(order.createdAt)}</TableCell>
                    <TableCell>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                        <TextField
                          select
                          size="small"
                          value={getDraftStatus(order)}
                          onChange={(event) =>
                            setStatusDrafts((prev) => ({
                              ...prev,
                              [order._id]: event.target.value
                            }))
                          }
                          sx={{ minWidth: 170 }}
                        >
                          {paymentStatuses.map((value) => (
                            <MenuItem key={value} value={value}>
                              {value}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={updatingOrderId === order._id}
                          onClick={() => onUpdatePaymentStatus(order)}
                        >
                          {updatingOrderId === order._id ? 'Updating...' : 'Update'}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {!selectedOrders.length && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No orders found for this user.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={selectedOrders.length}
            page={ordersPage}
            onPageChange={(_event, nextPage) => setOrdersPage(nextPage)}
            rowsPerPage={ordersRowsPerPage}
            onRowsPerPageChange={(event) => {
              setOrdersRowsPerPage(Number(event.target.value));
              setOrdersPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20, 50]}
          />
        </MainCard>
      )}

      {!loading && !error && selectedUser && viewMode === 'collections' && (
        <MainCard title={`Collections for ${getDisplayName(selectedUser)}`}>
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
              {getUserStatusChip(selectedUser)}
              {selectedUser.email && <Chip size="small" label={selectedUser.email} variant="outlined" />}
              {selectedUser.phone && <Chip size="small" label={selectedUser.phone} variant="outlined" />}
              {selectedUser.lastCollectionActivity && (
                <Chip size="small" label={`Last activity ${formatDateTime(selectedUser.lastCollectionActivity)}`} variant="outlined" />
              )}
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
              <DetailMetric label="Cart Items" value={selectedUser.cartSummary?.itemCount || 0} />
              <DetailMetric label="Wishlist Items" value={selectedUser.wishlistSummary?.itemCount || 0} />
              <DetailMetric
                label="Cart Total"
                value={
                  selectedUser.cartSummary?.hasCart
                    ? formatAmount(selectedUser.cartSummary?.currency || 'INR', selectedUser.cartSummary?.total || 0)
                    : 'No active cart'
                }
                tone="highlight"
              />
              <DetailMetric
                label="Last Login"
                value={selectedUser.lastLogin ? formatDateTime(selectedUser.lastLogin) : 'No login recorded'}
              />
            </Stack>

            {(listType === 'wishlist' || listType === 'cart') && (
              <Alert severity="info" variant="outlined">
                {listType === 'wishlist'
                  ? 'Cart metrics remain visible, but full cart items are not fetched while the wishlist-only filter is active.'
                  : 'Wishlist metrics remain visible, but full wishlist items are not fetched while the cart-only filter is active.'}
              </Alert>
            )}

            <Box>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Cart
              </Typography>
              <CartSection cart={selectedUser.cart} isSummaryOnly={listType === 'wishlist'} />
            </Box>

            <Divider />

            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="h6">Wishlist</Typography>
                <Chip size="small" variant="outlined" label={`${selectedUser.wishlistSummary?.itemCount || 0} items`} />
              </Stack>
              <WishlistSection wishlist={selectedUser.wishlist} isSummaryOnly={listType === 'cart'} />
            </Box>
          </Stack>
        </MainCard>
      )}

      {/* Role Assignment Popover */}
      <Popover
        open={Boolean(roleAnchorEl)}
        anchorEl={roleAnchorEl}
        onClose={() => {
          setRoleAnchorEl(null);
          setRoleChangeUserId(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ px: 1, py: 0.5, fontWeight: 700 }}>
            Assign User Role
          </Typography>
          <Divider sx={{ my: 0.75 }} />
          {updatingRole ? (
            <Typography variant="body2" sx={{ p: 1 }}>Updating role...</Typography>
          ) : (
            (roles || []).map((r) => (
              <MenuItem
                key={r.key || r._id}
                dense
                onClick={() => handleRoleSelect(r.key || r.name)}
                sx={{ borderRadius: 1, my: 0.25 }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {r.name}
                  </Typography>
                  <Chip size="small" label={r.key} variant="outlined" sx={{ ml: 1, fontSize: '0.65rem' }} />
                </Stack>
              </MenuItem>
            ))
          )}
        </Box>
      </Popover>

      {/* Staff User Generation Dialog */}
      <StaffUserDialog
        open={staffDialogOpen}
        onClose={() => setStaffDialogOpen(false)}
        roles={roles}
        onSuccess={() => {
          mutate();
        }}
      />
    </Stack>
  );
}
